import { grey } from '@ant-design/colors';
import {
  InfoCircleOutlined,
  PlusCircleOutlined,
  ReloadOutlined,
  RightOutlined,
  SendOutlined,
  TableOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client/react';
import { css } from '@emotion/css';
import { App, Button, Col, Flex, Menu, Result, Row, Steps, theme } from 'antd';
import type React from 'react';
import { lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useAuth } from 'react-oidc-context';
import { Navigate, useNavigate } from 'react-router';
import { useShallow } from 'zustand/react/shallow';
import { useLaunchOnly } from '../../ais/useAisCapabilities';
import { useAisIdentity } from '../../ais/useAisIdentity';
import { AiSparklesIcon } from '../../components/icons/AiSparklesIcon.tsx';
import { LazyDependency } from '../../components/LazyDependency.tsx';
import { LoadingIndicator } from '../../components/LoadingIndicator.tsx';
import { MainContent } from '../../components/MainContent';
import { showParamWarning } from '../../components/showParamWarning';
import { getFragmentData } from '../../graphql/generated';
import {
  HotKey,
  type ParameterSelection_ProfileFragment,
  type ParameterSelection_RequestableParameterFragment,
  type ParameterToAddInput,
  type PatientDataInput,
} from '../../graphql/generated/graphql.ts';
import { type BillingInfo, initialParamQueue, type SelectedParameter, useAppStore } from '../../hooks/store/useAppStore';
import { useCurrentContextStore } from '../../hooks/store/useCurrentContextStore.ts';
import { useRequestCopy } from '../../hooks/useRequestCopy';
import { getUniqueLabs } from '../../utils/labs';
import { insuranceName } from '../../utils/patient';
import { hasFrontDeskRole } from '../../utils/user.ts';
import { OpenRequestsBySvnrModal } from './OpenRequestsBySvnrModal';
import { ParameterInfoModal } from './ParameterInfoModal';
import {
  ADD_PARAMETERS_MUTATION,
  DoctorSheetFragment,
  OPEN_REQUESTS_BY_SVNR_QUERY,
  PARAMETER_SELECTION_QUERY,
  PATIENT_REQUESTS_QUERY,
  ProfileFragment,
  RequestableParameterFragment,
} from './ParameterSelection.gql';
import { PatientHeader } from './PatientHeader';
import { StaleRequestGuard } from './StaleRequestGuard.tsx';
import { InvalidAdditionalDataDialog } from './selection/InvalidAdditionalDataDialog.tsx';
import { LocalizationsChooserModal } from './selection/LocalizationsChooserModal.tsx';
import { NewProfileModal } from './selection/NewProfileModal';
import { MemoParameters } from './selection/Parameters.tsx';
import { MemoProfiles } from './selection/Profiles';
import { ReorderTabEntry } from './selection/ReorderTabEntry.tsx';
import { MemoRequests } from './selection/Requests';
import { SelectBillingModal } from './selection/SelectBillingModal';
import { MemoSelectedParams } from './selection/SelectedParams';
import { MemoSheets } from './selection/Sheets.tsx';
import { validateLocalizations } from './selection/validateLocalizations.ts';

const { useToken } = theme;

// @ant-design/x + x-markdown are ~180 kB and only needed when the tab is opened
const LazyAiAssistantTab = lazy(() => import('./ai/AiAssistantTab.tsx').then(m => ({ default: m.AiAssistantTab })));

type MenuKey = 'profile' | 'sheets' | 'parameter' | 'anforderungen' | 'reorder' | 'ai-assistant';

// Selecting a profile prepends its diagnose to the free-text field; dropping the profile takes
// that block back. Callers pass only the diagnoses they actually wrote themselves, so text the
// user typed is never a candidate for removal, and a block they have edited since is left alone
// because it is no longer found verbatim.
//
// A text is kept when a still-selected profile needs it: because the insertion guard is
// `includes`, that profile's own insertion may have been suppressed by this one, so removing
// the block now would leave it without its text — or cut a fragment out of its block.
const withoutProfileDiagnoses = (text: string, droppedDiagnoses: readonly string[], keptDiagnoses: readonly string[]) =>
  droppedDiagnoses.reduce((result, dropped) => {
    if (!dropped || keptDiagnoses.some(it => it.includes(dropped))) {
      return result;
    }
    const needle = [`${dropped}\n`, `\n${dropped}`, dropped].find(it => result.includes(it));
    return needle ? result.replace(needle, '') : result;
  }, text);

// fallow-ignore-next-line complexity
export const ParameterSelection: React.FC = () => {
  const { token } = useToken();
  const styles = useMemo(
    () => ({
      flexContainer: css`
        display: flex;
        flex-direction: column;
        height: 100%;
      `,
      growZero: css`
        flex-grow: 0;
      `,
      growFull: css`
        flex-grow: 1;
        overflow: hidden;
      `,
      leftCol: css`
        height: 100%;
      `,
      rightCol: css`
        height: 100%;
        border-left: 1px solid ${token.colorBorderSecondary};
      `,
    }),
    [token]
  );

  const {
    patientData,
    parameterIdsWithUnknownBilling,
    setParameterIdsWithUnknownBilling,
    requestIdForUpdate,
    setRequestIdForUpdate,
    requestIdForReorder,
    openRequestsModalEnabled,
    setOpenRequestsModalEnabled,
    paramQueue,
    setParamQueue,
    selectedProfiles,
    setSelectedProfiles,
    setExecuteRequestCopy,
    setAisLaunchIdForCreate,
    setRequestIdForReorder,
    setPatientData,
    additionalDataInvalidFields,
    localizations,
    preferredBillingType,
    diagnose,
    setDiagnose,
    aisImportResult,
    setAisImportResult,
    setComment,
  } = useAppStore(
    useShallow(state => ({
      patientData: state.patientData,
      parameterIdsWithUnknownBilling: state.parameterIdsWithUnknownBilling,
      setParameterIdsWithUnknownBilling: state.setParameterIdsWithUnknownBilling,
      requestIdForUpdate: state.requestIdForUpdate,
      setRequestIdForUpdate: state.setRequestIdForUpdate,
      requestIdForReorder: state.requestIdForReorder,
      openRequestsModalEnabled: state.openRequestsModalEnabled,
      setOpenRequestsModalEnabled: state.setOpenRequestsModalEnabled,
      paramQueue: state.paramQueue,
      setParamQueue: state.setParamQueue,
      selectedProfiles: state.selectedProfiles,
      setSelectedProfiles: state.setSelectedProfiles,
      setExecuteRequestCopy: state.setExecuteRequestCopy,
      setAisLaunchIdForCreate: state.setAisLaunchIdForCreate,
      setRequestIdForReorder: state.setRequestIdForReorder,
      setPatientData: state.setPatientData,
      additionalDataInvalidFields: state.additionalDataInvalidFields,
      localizations: state.localizations,
      preferredBillingType: state.preferredBillingType,
      diagnose: state.diagnose,
      setDiagnose: state.setDiagnose,
      aisImportResult: state.aisImportResult,
      setAisImportResult: state.setAisImportResult,
      setComment: state.setComment,
    }))
  );

  const { currentDoctorId, primaryDoctorId } = useCurrentContextStore();

  const navigate = useNavigate();
  const auth = useAuth();
  const [selectedMenuKeys, setSelectedMenuKeys] = useState<MenuKey[]>(['parameter']);
  const [newProfileModalOpen, setNewProfileModalOpen] = useState(false);
  const [localizationsChooserModalOpen, setLocalizationsChooserModalOpen] = useState(false);
  const { notification } = App.useApp();

  const isFrontDesk = hasFrontDeskRole(auth.user);

  const { data, loading, error, refetch } = useQuery(PARAMETER_SELECTION_QUERY, {
    variables: {
      patientData: patientData!,
      doctorId: currentDoctorId,
      mainDoctorId: isFrontDesk ? primaryDoctorId : currentDoctorId,
      requestIdForReorder: requestIdForReorder,
    },
    // Skip when AIS import has invalid patient fields — during the brief re-render before navigation
    // to PatientRegistration, the query would fire with invalid data (e.g. empty birthday) that the
    // GraphQL Date scalar rejects.
    skip: !patientData || !!aisImportResult?.invalidPatientFields?.length,
  });

  const profiles = getFragmentData(ProfileFragment, data?.profiles);
  const requestableParameters = getFragmentData(RequestableParameterFragment, data?.requestableParameters);
  const doctorSheets = getFragmentData(DoctorSheetFragment, data?.doctorSheets);

  // Owned here so the profile cards' counters and the auto-deselect below can never disagree
  // about which of a profile's parameters are requestable.
  const requestableByMainId = useMemo(() => {
    const map = new Map<string, ParameterSelection_RequestableParameterFragment>();
    for (const param of requestableParameters ?? []) {
      if (!map.has(param.mainParameterId)) {
        map.set(param.mainParameterId, param);
      }
    }
    return map;
  }, [requestableParameters]);

  const labsFromParams = useMemo(() => getUniqueLabs(requestableParameters ?? []), [requestableParameters]);
  const labIdToIdentifier = useMemo(() => new Map(labsFromParams.map(l => [l.id, l.identifier])), [labsFromParams]);
  const labIdentifierToName = useMemo(() => new Map(labsFromParams.map(l => [l.identifier, l.shortName])), [labsFromParams]);
  const hasAiEnabled = labsFromParams.some(l => l.aiEnabled) && data?.doctor?.aiEnabled === true;

  const { data: patientRequests } = useQuery(PATIENT_REQUESTS_QUERY, {
    variables: {
      doctorId: currentDoctorId,
      svnr: patientData?.svnr ?? '',
    },
    skip: !patientData?.svnr,
  });

  const launchOnly = useLaunchOnly();
  const aisIdentity = useAisIdentity();
  const launchCreate = useAppStore(state => launchOnly && !!state.aisLaunchIdForCreate);
  const reorderPossible =
    !requestIdForReorder && !requestIdForUpdate && (patientRequests?.patientRequests?.some(it => it.reorderable) ?? false);

  const flipParamNames = data?.doctor?.flipParamNames ?? false;

  useRequestCopy('update', false, patientData, copyData => {
    if (copyData.unknownBillingParameters.length) {
      showParamWarning(
        notification,
        'Verrechnungssituation geändert',
        'Für folgende Parameter hat sich die Verrechnungssituation geändert:',
        copyData.unknownBillingParameters.map(it => (flipParamNames ? it.longName : it.shortName))
      );
    }
    if (copyData.unavailableParameters.length) {
      showParamWarning(
        notification,
        'Parameter nicht verfügbar',
        'Folgende Parameter stehen nicht zur Verfügung:',
        copyData.unavailableParameters
      );
    }
  });

  useEffect(() => {
    if (!data) {
      return;
    }

    const hasSheets = (data.doctorSheets?.length ?? 0) > 0;
    const hasProfiles = (data.profiles?.length ?? 0) > 0;
    let menuKey: MenuKey = 'parameter';

    if (hasSheets) {
      menuKey = 'sheets';
    }
    if (hasProfiles) {
      menuKey = 'profile';
    }
    setSelectedMenuKeys([menuKey]);
  }, [data]);

  useHotkeys(
    Object.keys(HotKey).map(it => it.toLowerCase()),
    (_, event) => {
      if (event?.keys?.length && profiles?.length) {
        const profile = profiles.find(it => it.hotKey === event.keys![0].toUpperCase());
        if (profile) {
          addProfile(profile);
        }
      }
    },
    {
      preventDefault: true,
      enableOnFormTags: ['input', 'select', 'textarea'],
      enableOnContentEditable: true,
    }
  );

  const [openRequestModalVisible, setOpenRequestModalVisible] = useState(false);
  const { data: openRequestsAllData } = useQuery(OPEN_REQUESTS_BY_SVNR_QUERY, {
    variables: {
      doctorId: currentDoctorId,
      svnr: patientData?.svnr ?? '',
    },
    skip: !!requestIdForUpdate || !!requestIdForReorder || !patientData?.svnr || isFrontDesk,
  });
  // a launch creating an Anforderung offers only those the AIS knows, so that it does not lose track of the other ones
  const openRequests = (openRequestsAllData?.openRequests ?? []).filter(
    it => !launchCreate || (!!it.aisVendor && it.aisVendor === aisIdentity?.vendor)
  );
  useEffect(() => {
    setOpenRequestModalVisible(!!data && !!openRequests.length && openRequestsModalEnabled && !additionalDataInvalidFields.length);
  }, [data, openRequests.length, openRequestsModalEnabled, additionalDataInvalidFields]);

  const [addParameterMutation] = useMutation(ADD_PARAMETERS_MUTATION);
  // one AddParameters at a time; what is queued meanwhile goes out with the next one
  const addInFlight = useRef(false);

  const [chooseBillingParam, setChooseBillingParam] = useState<ParameterSelection_RequestableParameterFragment | null>(null);
  const [billingModalCloseable, setBillingModalCloseable] = useState(true);
  const [infoParam, setInfoParam] = useState<ParameterSelection_RequestableParameterFragment | null>(null);
  const [defaultBillingInfo, setDefaultBillingInfo] = useState<BillingInfo | null>(null);

  useEffect(() => {
    // abort when billing chooser is required or no patient data is present
    if (chooseBillingParam !== null || !patientData) {
      return;
    }

    // abort when there are no more parameters to add
    if (!paramQueue.parameters.length) {
      if (paramQueue.ignoredParameters.length) {
        setParamQueue(state => ({ ...state.paramQueue, ignoredParameters: [] }));
      }
      return;
    }

    if (addInFlight.current) {
      return;
    }
    addInFlight.current = true;
    const sent = paramQueue.parameters;

    (async () => {
      const result = await addParameterMutation({
        variables: {
          input: {
            parameterInputs: sent,
            selectedParameters: paramQueue.selectedParameters.map(sp => ({
              parameterId: sp.id,
              parameterShortName: sp.shortName,
              parameterLongName: sp.longName,
              billingType: sp.billingInfo.billingType,
              specialRateId: sp.billingInfo.specialRateId,
              diagnoseId: sp.billingInfo.diagnoseId,
            })),
            requestIdForReorder: requestIdForReorder,
            doctorId: currentDoctorId,
            patientData: patientData,
            chooseBilling: paramQueue.chooseBilling,
          },
        },
      }).finally(() => {
        addInFlight.current = false;
      });

      if (result.data?.addParameters?.incompatibleParams?.length) {
        showParamWarning(
          notification,
          'Inkompatible Parameter',
          'Folgende Parameter können nicht angefordert werden, da sie mit der aktuellen Auswahl nicht kompatibel sind:',
          result.data.addParameters.incompatibleParams,
          result.data.addParameters.incompatibleParams.join() // computed key to show the notification only once with the same entries
        );
      }

      const paramsToAdd =
        result.data?.addParameters?.paramsToAdd?.filter(
          paramToAdd => !paramQueue.ignoredParameters.some(ignoredParam => ignoredParam.id === paramToAdd.parameterId)
        ) ?? [];

      const firstParamWithoutBilling = paramsToAdd.find(it => !it.billingInfo);
      if (firstParamWithoutBilling) {
        const requestableParam = requestableParameters?.find(p => p.id === firstParamWithoutBilling.parameterId);
        if (requestableParam) {
          setChooseBillingParam(requestableParam);
        }
      }

      setParamQueue(state => {
        const selectedParameterIds = state.paramQueue.selectedParameters.map(it => it.id);

        return {
          ...state.paramQueue,
          parameters: [
            ...paramsToAdd
              .filter(p => p.billingInfo === null)
              .map(p => ({ parameterId: p.parameterId, preferredBillingType: null, preferredDiagnoseIds: [] })),
            ...state.paramQueue.parameters.filter(it => !sent.includes(it)),
          ],
          selectedParameters: [
            // prepend new params -> default sorting
            ...paramsToAdd
              .filter(p => p.billingInfo !== null)
              .filter(p => !selectedParameterIds.includes(p.parameterId)) // do not allow duplicates
              .flatMap(p => {
                const param = requestableParameters?.find(rp => rp.id === p.parameterId);
                return param
                  ? [
                      {
                        ...param,
                        billingInfo: p.billingInfo!,
                      },
                    ]
                  : [];
              })
              .sort((a, b) => (flipParamNames ? a.longName.localeCompare(b.longName) : a.shortName.localeCompare(b.shortName))),
            ...state.paramQueue.selectedParameters,
          ],
        };
      });
    })();
  }, [
    paramQueue,
    setParamQueue,
    currentDoctorId,
    chooseBillingParam,
    patientData,
    addParameterMutation,
    requestIdForReorder,
    notification,
    requestableParameters,
    flipParamNames,
  ]);

  useEffect(() => {
    // remove already selected params which cannot be requested
    const withoutParamIds = paramQueue.selectedParameters.flatMap(it => it.withoutParameterIds);
    const filteredParams = paramQueue.selectedParameters.filter(sp => !withoutParamIds.includes(sp.id));
    const invalidParams = paramQueue.selectedParameters
      .filter(sp => withoutParamIds.includes(sp.id))
      .map(it => (flipParamNames ? it.longName : it.shortName));

    if (paramQueue.selectedParameters.length !== filteredParams.length) {
      showParamWarning(
        notification,
        'Inkompatible Parameter',
        'Folgende Parameter wurden entfernt, da sie mit den ausgewählten Parametern inkompatibel sind:',
        invalidParams,
        'incompatible-params'
      );
      setParamQueue(state => ({ ...state.paramQueue, selectedParameters: filteredParams }));
    }
  }, [paramQueue, setParamQueue, notification, flipParamNames]);

  const onEnqueue = useCallback(
    (params: ParameterToAddInput[], chooseBilling: boolean) => {
      setParamQueue(state => {
        const selectedParamIds = state.paramQueue.selectedParameters.map(it => it.id);
        return {
          ...state.paramQueue,
          parameters: [...state.paramQueue.parameters, ...params.filter(it => !selectedParamIds.includes(String(it.parameterId)))],
          chooseBilling: chooseBilling,
        };
      });
    },
    [setParamQueue]
  );

  const addProfile = useCallback(
    (profile: ParameterSelection_ProfileFragment) => {
      const filteredInputs: ParameterToAddInput[] = [];
      const unavailableParams: string[] = [];
      profile.parameters.forEach(profileParameter => {
        const reqParam = requestableParameters?.find(
          it => it.lab.id === profileParameter.parameter.lab.id && it.shortName === profileParameter.parameter.shortName
        );
        if (reqParam) {
          filteredInputs.push({
            parameterId: reqParam.id,
            preferredBillingType: preferredBillingType ?? profileParameter.preferredBillingType,
            preferredDiagnoseIds: profile.diagnoses.map(it => it.id),
          });
        } else {
          unavailableParams.push(flipParamNames ? profileParameter.parameter.longName : profileParameter.parameter.shortName);
        }
      });

      if (unavailableParams.length) {
        showParamWarning(notification, 'Parameter nicht verfügbar', 'Folgende Parameter stehen nicht zur Verfügung:', unavailableParams);
      }

      const diagnoseInserted = !!profile.diagnose && !diagnose.includes(profile.diagnose);
      if (diagnoseInserted) {
        setDiagnose(diagnose ? `${profile.diagnose}\n${diagnose}` : profile.diagnose!);
      }

      // Queue the parameters before marking the profile as selected: should the two land in
      // separate renders, the intermediate state is "queue busy, profile not yet selected",
      // which the auto-deselect below skips — the reverse order would drop the fresh selection.
      onEnqueue(filteredInputs, false);

      if (!selectedProfiles.some(it => it.id === profile.id)) {
        setSelectedProfiles([...selectedProfiles, { id: profile.id, diagnoseInserted }]);
      }
    },
    [
      diagnose,
      flipParamNames,
      notification,
      onEnqueue,
      preferredBillingType,
      requestableParameters,
      selectedProfiles,
      setDiagnose,
      setSelectedProfiles,
    ]
  );

  const selectedProfileIds = useMemo(() => selectedProfiles.map(it => it.id), [selectedProfiles]);

  const diagnoseOf = useCallback((profileId: string) => profiles?.find(it => it.id === profileId)?.diagnose ?? '', [profiles]);

  const onDeselectProfile = useCallback(
    (profile: ParameterSelection_ProfileFragment) => {
      const dropped = selectedProfiles.find(it => it.id === profile.id);
      const kept = selectedProfiles.filter(it => it.id !== profile.id);
      setSelectedProfiles(kept);
      if (dropped?.diagnoseInserted) {
        setDiagnose(
          withoutProfileDiagnoses(
            diagnose,
            [diagnoseOf(profile.id)],
            kept.map(it => diagnoseOf(it.id))
          )
        );
      }
    },
    [diagnose, diagnoseOf, selectedProfiles, setDiagnose, setSelectedProfiles]
  );

  // Everything that drops the whole profile selection at once goes through the same take-back
  // as a single deselect — otherwise the diagnoses of dropped profiles would pile up.
  const clearProfileSelection = useCallback(() => {
    const dropped = selectedProfiles.filter(it => it.diagnoseInserted).map(it => diagnoseOf(it.id));
    setSelectedProfiles([]);
    setDiagnose(withoutProfileDiagnoses(diagnose, dropped, []));
  }, [diagnose, diagnoseOf, selectedProfiles, setDiagnose, setSelectedProfiles]);

  useEffect(() => {
    // A profile that has lost its last parameter is not selected any more — no matter whether the
    // user removed them one by one or the incompatibility check took them out. Dropping it here
    // takes its diagnose back through the same path as an explicit deselect, so no diagnose can
    // outlive the parameters it was meant for. Waits for the add queue to drain, since a freshly
    // clicked profile has no parameters yet while they are still on their way in.
    if (!selectedProfiles.length || paramQueue.parameters.length || !profiles) {
      return;
    }

    const selectedParamIds = new Set(paramQueue.selectedParameters.map(it => it.id));
    const hasSelectedParameter = (profile: ParameterSelection_ProfileFragment) =>
      profile.parameters.some(it => {
        const requestable = requestableByMainId.get(it.parameter.id);
        return !!requestable && selectedParamIds.has(requestable.id);
      });

    const emptied = selectedProfiles.filter(entry => {
      const profile = profiles.find(it => it.id === entry.id);
      if (!profile) {
        // not in the loaded list (deleted or another lab's) — nothing to judge it by, so keep it
        return false;
      }
      return !hasSelectedParameter(profile);
    });

    if (!emptied.length) {
      return;
    }

    const kept = selectedProfiles.filter(it => !emptied.includes(it));
    setSelectedProfiles(kept);
    setDiagnose(
      withoutProfileDiagnoses(
        diagnose,
        emptied.filter(it => it.diagnoseInserted).map(it => diagnoseOf(it.id)),
        kept.map(it => diagnoseOf(it.id))
      )
    );
  }, [diagnose, diagnoseOf, paramQueue, profiles, requestableByMainId, selectedProfiles, setDiagnose, setSelectedProfiles]);

  useEffect(() => {
    if (!data || !aisImportResult || !profiles || !requestableParameters) {
      return;
    }

    if (!aisImportResult.profiles.length && !aisImportResult.parameters.length) {
      return;
    }

    const resolved = aisImportResult.parameters.map(ref => ({
      ref,
      param: requestableParameters.find(it => it.shortName === ref.shortName && (!ref.labId || it.lab.identifier === ref.labId)),
    }));
    const unavailableParams = resolved
      .filter(it => !it.param)
      .map(({ ref }) => (ref.labId ? `${ref.shortName} (${ref.labId})` : ref.shortName));
    const paramsToAdd = resolved.flatMap(({ param }) =>
      param ? [{ parameterId: param.id, preferredBillingType: preferredBillingType, preferredDiagnoseIds: [] }] : []
    );

    if (paramsToAdd.length) {
      onEnqueue(paramsToAdd, false);
    }

    if (unavailableParams.length) {
      showParamWarning(
        notification,
        'Extern übergebene Parameter nicht verfügbar',
        'Die vom externen System übergebenen Parameter sind nicht verfügbar',
        unavailableParams
      );
    }

    // one profile per pass: addProfile reads the diagnose and the selection of the current render
    const [profileName, ...laterProfiles] = aisImportResult.profiles;
    if (profileName) {
      const profile = profiles.find(it => it.name === profileName);
      if (profile) {
        addProfile(profile);
      } else {
        showParamWarning(
          notification,
          'Extern übergebenes Profil nicht verfügbar',
          'Das vom externen System übergebene Profil ist nicht verfügbar',
          [profileName]
        );
      }
    }

    setAisImportResult({
      ...aisImportResult,
      profiles: laterProfiles,
      parameters: [],
    });
  }, [
    data,
    aisImportResult,
    profiles,
    requestableParameters,
    setAisImportResult,
    preferredBillingType,
    onEnqueue,
    addProfile,
    notification,
  ]);

  const onRemove = useCallback(
    (paramId: string) => {
      setParamQueue(state => ({
        ...state.paramQueue,
        selectedParameters: state.paramQueue.selectedParameters.filter(p => p.id !== paramId),
      }));
    },
    [setParamQueue]
  );

  const onClear = useCallback(() => {
    setParamQueue(() => initialParamQueue);
    clearProfileSelection();
  }, [setParamQueue, clearProfileSelection]);

  const onEdit = useCallback(
    (param: SelectedParameter) => {
      setParamQueue(state => ({
        ...state.paramQueue,
        selectedParameters: state.paramQueue.selectedParameters.filter(p => p.id !== param.id),
      }));
      setDefaultBillingInfo(param.billingInfo);
      setBillingModalCloseable(false);
      const requestableParamId = requestableParameters?.find(p => p.id === param.id)?.id;
      if (requestableParamId) {
        onEnqueue([{ parameterId: requestableParamId, preferredBillingType: null, preferredDiagnoseIds: [] }], true);
      }
    },
    [setParamQueue, requestableParameters, onEnqueue]
  );

  const onEditPatient = useCallback(() => {
    navigate('/anforderung/patient-erfassung');
  }, [navigate]);

  const onNext = useCallback(() => {
    if (validateLocalizations(paramQueue.selectedParameters, localizations, !!requestIdForReorder).length > 0) {
      setLocalizationsChooserModalOpen(true);
      return;
    }
    navigate('/anforderung/uebersicht');
  }, [localizations, navigate, paramQueue, requestIdForReorder]);

  const onInfo = useCallback((param: ParameterSelection_RequestableParameterFragment) => {
    setInfoParam(param);
  }, []);

  const onEditRequest = useCallback(
    (requestId: string) => {
      setRequestIdForReorder('');
      setExecuteRequestCopy(true);
      setOpenRequestsModalEnabled(false);
      setAisLaunchIdForCreate(undefined);
      setRequestIdForUpdate(requestId);
    },
    [setAisLaunchIdForCreate, setExecuteRequestCopy, setOpenRequestsModalEnabled, setRequestIdForReorder, setRequestIdForUpdate]
  );

  const onOverrideRequest = useCallback(
    (requestId: string) => {
      setRequestIdForReorder('');
      setOpenRequestsModalEnabled(false);
      setRequestIdForUpdate(requestId);
    },
    [setOpenRequestsModalEnabled, setRequestIdForReorder, setRequestIdForUpdate]
  );

  const onReorderRequest = useCallback(
    (requestId: string, comment: string, patientData: PatientDataInput) => {
      setPatientData(patientData);
      setComment(comment);
      setParamQueue(() => initialParamQueue);
      clearProfileSelection();
      setRequestIdForUpdate('');
      setRequestIdForReorder(requestId);
    },
    [setParamQueue, clearProfileSelection, setPatientData, setRequestIdForReorder, setRequestIdForUpdate, setComment]
  );

  const onAddSheetOrRequestsParams = useCallback(
    (params: ParameterToAddInput[]) =>
      onEnqueue(
        params.map(it => ({
          ...it,
          preferredBillingType: preferredBillingType ?? it.preferredBillingType,
        })),
        false
      ),
    [onEnqueue, preferredBillingType]
  );

  const onAddParameterViewParam = useCallback(
    (param: ParameterToAddInput, chooseBilling: boolean) =>
      onEnqueue([{ ...param, preferredBillingType: preferredBillingType ?? param.preferredBillingType }], chooseBilling),
    [onEnqueue, preferredBillingType]
  );

  const onAddProfileParam = useCallback(
    (param: ParameterToAddInput) =>
      onEnqueue([{ ...param, preferredBillingType: preferredBillingType ?? param.preferredBillingType }], false),
    [onEnqueue, preferredBillingType]
  );

  useEffect(() => {
    if (data?.requestableParameters && parameterIdsWithUnknownBilling.length) {
      onEnqueue(
        parameterIdsWithUnknownBilling.map(it => ({
          parameterId: it,
          preferredBillingType: null,
          preferredDiagnoseIds: [],
        })),
        false
      );
      setParameterIdsWithUnknownBilling([]);
    }
  }, [data, onEnqueue, parameterIdsWithUnknownBilling, setParameterIdsWithUnknownBilling]);

  const insurance = useMemo(() => insuranceName(patientData ?? { insuranceCode: '' }, data?.insurances ?? []), [data, patientData]);

  if (patientData === null) {
    return <Navigate replace to="/" />;
  }

  return (
    <MainContent size="large" heightType="fullHeight">
      <div className={styles.flexContainer}>
        <Steps
          className={styles.growZero}
          items={[
            {
              status: 'finish',
              title: 'Patient',
              icon: <UserOutlined />,
            },
            {
              status: 'process',
              title: 'Parameterauswahl',
              icon: <TableOutlined />,
            },
            {
              status: 'wait',
              title: requestIdForUpdate
                ? 'Bestehende Anforderung abschließen'
                : requestIdForReorder
                  ? 'Nachforderung abschließen'
                  : 'Neue Anforderung abschließen',
              icon: <SendOutlined />,
            },
          ]}
        />

        {error ? (
          <Result
            status={500}
            title="Leider ist ein Fehler aufgetreten"
            subTitle="Bitte versuchen Sie eine neue Anforderung zu erstellen. Kontaktieren Sie bitte unseren Support, wenn das Problem weiterhin auftritt."
            extra={
              !launchOnly && (
                <Button icon={<ReloadOutlined />} onClick={() => navigate('/anforderung')} type="primary">
                  Neue Anforderung erstellen
                </Button>
              )
            }
          />
        ) : loading ? (
          <LoadingIndicator height="200px" />
        ) : (
          <Row gutter={[32, 0]} className={styles.growFull}>
            <Col xs={12} sm={12} md={12} lg={16} xl={16} className={styles.leftCol}>
              <div className={styles.flexContainer}>
                <PatientHeader
                  patientData={patientData}
                  insuranceName={insurance}
                  patientBilling={data?.doctor?.patientBilling || false}
                  showDefaultBillingChooser={true}
                  availableBillingTypes={requestableParameters?.flatMap(it => it.billingInfos).map(it => it.billingType) ?? []}
                  onEdit={!requestIdForReorder ? onEditPatient : undefined}
                  menu={
                    <Menu
                      selectedKeys={selectedMenuKeys}
                      onSelect={info => setSelectedMenuKeys(info.selectedKeys as MenuKey[])}
                      mode="horizontal"
                      className={css`
                        background-color: ${token.Layout?.bodyBg};
                      `}
                      items={[
                        {
                          key: 'profile',
                          label: 'Profile',
                        },
                        {
                          key: 'sheets',
                          label: 'Sheets',
                        },
                        {
                          key: 'parameter',
                          label: 'Alle Parameter',
                        },
                        ...(patientData.svnr
                          ? [
                              {
                                key: 'anforderungen',
                                label: 'Zuletzt angefordert',
                              },
                            ]
                          : []),
                        ...(hasAiEnabled
                          ? [
                              {
                                key: 'ai-assistant' as const,
                                label: (
                                  <Flex align="center" gap="small">
                                    <AiSparklesIcon size={16} /> KI-Assistent
                                  </Flex>
                                ),
                              },
                            ]
                          : []),
                        ...(reorderPossible
                          ? [
                              {
                                key: 'reorder',
                                label: <ReorderTabEntry tabKey="reorder" selectedTabKeys={selectedMenuKeys} />,
                              },
                            ]
                          : []),
                      ]}
                    />
                  }
                />
                {data?.requestableParameters?.length ? (
                  <>
                    {selectedMenuKeys[0] === 'profile' && (
                      <MemoProfiles
                        params={requestableParameters ?? []}
                        requestableByMainId={requestableByMainId}
                        selectedParams={paramQueue.selectedParameters}
                        selectedProfileIds={selectedProfileIds}
                        onAddProfile={addProfile}
                        onDeselectProfile={onDeselectProfile}
                        flipParamNames={flipParamNames}
                        profiles={profiles ?? []}
                        onRemove={onRemove}
                        onAddParam={onAddProfileParam}
                        onInfo={onInfo}
                        processing={!!paramQueue.parameters.length}
                      />
                    )}
                    {selectedMenuKeys[0] === 'sheets' && (
                      <MemoSheets
                        sheets={doctorSheets ?? []}
                        params={requestableParameters ?? []}
                        selectedParams={paramQueue.selectedParameters}
                        reorder={!!requestIdForReorder}
                        flipParamNames={flipParamNames}
                        onAdd={onAddSheetOrRequestsParams}
                        onRemove={onRemove}
                        onInfo={onInfo}
                      />
                    )}
                    {(selectedMenuKeys[0] === 'anforderungen' || selectedMenuKeys[0] === 'reorder') && (
                      <MemoRequests
                        params={requestableParameters ?? []}
                        onAdd={onAddSheetOrRequestsParams}
                        onReorder={onReorderRequest}
                        onUpdate={onEditRequest}
                        processing={!!paramQueue.parameters.length}
                        reordersOnly={selectedMenuKeys[0] === 'reorder'}
                        flipParamNames={flipParamNames}
                      />
                    )}
                    {selectedMenuKeys[0] === 'parameter' && (
                      <MemoParameters
                        flipParamNames={flipParamNames}
                        params={requestableParameters ?? []}
                        selectedParams={paramQueue.selectedParameters}
                        onAdd={onAddParameterViewParam}
                        onRemove={onRemove}
                        onEdit={onEdit}
                        onInfo={onInfo}
                      />
                    )}
                    {selectedMenuKeys[0] === 'ai-assistant' && patientData && (
                      <LazyDependency>
                        <LazyAiAssistantTab
                          patientData={patientData}
                          doctorId={currentDoctorId}
                          requestIdForReorder={requestIdForReorder || undefined}
                          requestableParameters={requestableParameters ?? []}
                          selectedParamIds={paramQueue.selectedParameters.map(p => p.id)}
                          currentlySelectedParameters={paramQueue.selectedParameters.map(p => ({
                            shortName: p.shortName,
                            labId: labIdToIdentifier.get(p.lab.id) ?? '',
                          }))}
                          onAdd={onAddSheetOrRequestsParams}
                          onRemove={onRemove}
                          onInfo={onInfo}
                          flipParamNames={flipParamNames}
                          labIdToIdentifier={labIdToIdentifier}
                          labIdentifierToName={labIdentifierToName}
                        />
                      </LazyDependency>
                    )}
                  </>
                ) : requestIdForReorder ? (
                  <Result
                    status="info"
                    icon={<InfoCircleOutlined style={{ color: grey[3] }} />}
                    extra={
                      !launchOnly && (
                        <Button
                          type="primary"
                          icon={<PlusCircleOutlined />}
                          onClick={() => {
                            setRequestIdForReorder('');
                          }}
                        >
                          Neue Anforderung für Patient starten
                        </Button>
                      )
                    }
                    title="Keine Parameter nachforderbar"
                    subTitle="Bitte starten Sie eine neue Anforderung, um für den Patienten Parameter auszuwählen"
                  />
                ) : (
                  <Result
                    status="info"
                    icon={<InfoCircleOutlined style={{ color: grey[3] }} />}
                    extra={
                      !launchOnly && (
                        <Button type="primary" icon={<PlusCircleOutlined />} onClick={() => navigate('/anforderung')}>
                          Neue Anforderung starten
                        </Button>
                      )
                    }
                    title="Keine Parameter anforderbar"
                    subTitle="Bitte starten Sie eine neue Anforderung"
                  />
                )}
              </div>
            </Col>
            <Col xs={12} sm={12} md={12} lg={8} xl={8} className={styles.rightCol}>
              <div className={styles.flexContainer}>
                <MemoSelectedParams
                  showPrices={data?.doctor?.showPrices ?? true}
                  flipParamNames={flipParamNames}
                  requestIdForReorder={requestIdForReorder}
                  selectedParams={paramQueue.selectedParameters}
                  showPriceSuggestion={data?.doctor?.showPriceSuggestion ?? false}
                  onNext={onNext}
                  onClear={onClear}
                  onEdit={onEdit}
                  onRemove={onRemove}
                  onInfo={paramId => {
                    const param = requestableParameters?.find(rp => rp.id === paramId);
                    if (param) {
                      onInfo(param);
                    }
                  }}
                  onNewProfile={() => setNewProfileModalOpen(true)}
                />
              </div>
            </Col>
          </Row>
        )}
      </div>
      <StaleRequestGuard />
      <SelectBillingModal
        parameter={chooseBillingParam}
        defaultSelected={defaultBillingInfo}
        forceSelection={!billingModalCloseable}
        skipEnabled={paramQueue.parameters.length > 1}
        onCancel={() => {
          setParamQueue(state => ({ ...state.paramQueue, parameters: [] }));
          setChooseBillingParam(null);
          setDefaultBillingInfo(null);
          setBillingModalCloseable(true);
        }}
        onSkip={() => {
          setParamQueue(state => ({
            ...state.paramQueue,
            ignoredParameters: [...state.paramQueue.ignoredParameters, chooseBillingParam!],
          }));
          setChooseBillingParam(null);
          setDefaultBillingInfo(null);
          setBillingModalCloseable(true);
        }}
        onSelect={(parameter, billingInfo) => {
          const selectedParameter = { ...parameter, billingInfo: billingInfo };
          setParamQueue(state => ({
            ...state.paramQueue,
            selectedParameters: [
              // prepend new params -> default sorting
              selectedParameter,
              ...state.paramQueue.selectedParameters.filter(it => it.id !== selectedParameter.id),
            ],
          }));
          setChooseBillingParam(null);
          setDefaultBillingInfo(null);
          setBillingModalCloseable(true);
        }}
        flipParamNames={flipParamNames}
        showPrices={data?.doctor?.showPrices ?? true}
      />
      <ParameterInfoModal
        parameter={infoParam}
        onClose={() => setInfoParam(null)}
        flipParamNames={flipParamNames}
        showPrices={data?.doctor?.showPrices ?? true}
      />
      <OpenRequestsBySvnrModal
        flipParamNames={flipParamNames}
        open={openRequestModalVisible}
        requestsFragment={openRequests}
        overrideRequestEnabled={(data?.doctor?.overrideRequestEnabled ?? false) && !launchCreate}
        onNew={() => {
          setOpenRequestsModalEnabled(false);
          setOpenRequestModalVisible(false);
        }}
        onEdit={onEditRequest}
        overrideRequest={onOverrideRequest}
      />
      <NewProfileModal
        selectedParams={paramQueue.selectedParameters}
        usedHotKeys={profiles?.filter(it => it.hotKey)?.map(it => it.hotKey!) ?? []}
        doctorId={isFrontDesk ? primaryDoctorId : currentDoctorId}
        open={newProfileModalOpen}
        onClose={() => setNewProfileModalOpen(false)}
        onSuccess={() => {
          setNewProfileModalOpen(false);
          refetch();
        }}
      />
      <InvalidAdditionalDataDialog invalidFields={additionalDataInvalidFields} onCancel={() => navigate('/anforderung')} />
      <LocalizationsChooserModal
        selectedParams={paramQueue.selectedParameters}
        requestIdForReorder={requestIdForReorder}
        open={localizationsChooserModalOpen}
        okText="Weiter zur Übersicht"
        okIcon={<RightOutlined />}
        closable={true}
        flipParamNames={flipParamNames}
        onClose={() => setLocalizationsChooserModalOpen(false)}
        onSuccess={() => {
          setLocalizationsChooserModalOpen(false);
          navigate('/anforderung/uebersicht');
        }}
      />
    </MainContent>
  );
};
